import { useState, useMemo } from 'react';

export type SortDir = 'asc' | 'desc';
export interface SortState { key: string | null; dir: SortDir; }

type SortValue = string | number | null | undefined;

/**
 * Client-side single-column sort for lists that are already fully loaded.
 *
 * `accessors` maps a column key to a value getter. Clicking a header toggles
 * asc → desc; a different header starts at asc. Empty/null values always sort
 * last (both directions). Strings use a numeric-aware locale compare so
 * "Inv-2" < "Inv-10".
 */
export function useTableSort<T>(
    rows: T[],
    accessors: Record<string, (row: T) => SortValue>,
    initial: SortState = { key: null, dir: 'asc' },
) {
    const [sort, setSort] = useState<SortState>(initial);

    const toggleSort = (key: string) =>
        setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

    const sorted = useMemo(() => {
        if (!sort.key) return rows;
        const acc = accessors[sort.key];
        if (!acc) return rows;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = acc(a);
            const bv = acc(b);
            const aNil = av === null || av === undefined || av === '';
            const bNil = bv === null || bv === undefined || bv === '';
            if (aNil && bNil) return 0;
            if (aNil) return 1;   // nils last regardless of direction
            if (bNil) return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * dir;
        });
        // accessors is a stable literal per render; intentionally excluded
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, sort]);

    return { sorted, sort, toggleSort };
}
